import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Expandable "Get It Today" retailer menu.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - Toggle button.
 * @property {HTMLElement} panel - Link list panel.
 *
 * @extends {Component<Refs>}
 */
class GetItTodayDropdown extends Component {
  requiredRefs = ['button', 'panel'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.megaMenuHover, this.close);

    // The panel stays laid out (just visually hidden) even while [hidden] -
    // see .git-dropdown__menu in the stylesheet - so its natural width can
    // be tracked continuously via ResizeObserver (across resize, web font
    // load, and content changes) without ever forcing a synchronous layout
    // read the way a manual getBoundingClientRect() measurement would.
    this.#resizeObserver = new ResizeObserver(this.#handlePanelResize);
    this.#resizeObserver.observe(this.refs.panel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.megaMenuHover, this.close);
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keyup', this.#handleKeyUp);
    this.#resizeObserver?.disconnect();
  }

  /**
   * The panel is absolutely positioned, so it never contributes to the
   * toggle button's width. Feed its measured width back in as a min-width,
   * so the button and the panel it opens share one edge.
   * @param {ResizeObserverEntry[]} entries
   */
  #handlePanelResize = ([entry]) => {
    const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;

    if (width > 0) {
      this.style.setProperty('--git-dropdown-panel-width', `${Math.ceil(width)}px`);
    }
  };

  get isOpen() {
    return this.refs.button.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Toggles the retailer link menu.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  };

  open() {
    if (this.isOpen) return;

    this.refs.button.setAttribute('aria-expanded', 'true');
    this.refs.panel.removeAttribute('hidden');
    this.classList.add('is-open');

    document.addEventListener('click', this.#handleClickOutside);
    document.addEventListener('keyup', this.#handleKeyUp);
  }

  close = () => {
    if (!this.isOpen) return;

    this.refs.button.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.classList.remove('is-open');

    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keyup', this.#handleKeyUp);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.close();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.close();
      this.refs.button.focus();
    }
  };
}

if (!customElements.get('get-it-today-dropdown')) {
  customElements.define('get-it-today-dropdown', GetItTodayDropdown);
}
